import * as Sentry from '@sentry/browser'

/**
 * Attendance tristate of the meeting editor: every participant row offers
 * present/excused/absent plus an explicit "clear" that puts the row back to
 * "not recorded yet".
 *
 * The whole tristate lives in LOCAL Alpine state so a manager can click through
 * the complete list without a Livewire round trip per click. Only a pause of
 * `debounce` milliseconds after the LAST click persists the map — there is
 * deliberately no save button any more.
 *
 * Leaving the list (pointer or focus) flushes a still pending save right away,
 * so every other interaction — completing the meeting, re-resolving the
 * participants, switching a tab — already works on the persisted state instead
 * of racing the debounce.
 *
 * The selection is handed to Livewire as a saveAttendance() PARAMETER; it is
 * never written through `$wire` properties (Alpine's reactive proxy layered
 * over Livewire's $wire proxy swallows writes silently, see rosterCirclePicker).
 */
export default ({
  attendance = {},
  allowClear = true,
  errorTitle = 'Error',
  errorMessage = 'Save failed',
  saveMethod = 'saveAttendance',
  debounce = 1000
}) => {
  // Only the component ID (a string) lives in the closure — the $wire proxy
  // itself is re-fetched per use and NEVER stored (double-proxy trap).
  let wireId = null
  let stopListening = null

  return {
    // PHP arrays with integer keys serialize as objects — normalize to string keys
    attendance: Object.fromEntries(
      Object.entries(attendance ?? {}).map(([key, value]) => [String(key), value ?? null])
    ),
    allowClear,
    errorTitle,
    errorMessage,
    saving: false,
    savedFlash: false,
    _saveTimer: null,
    _flashTimer: null,
    _saveGeneration: 0,
    _saveChain: null,

    init() {
      const root = this.$root && this.$root.closest ? this.$root.closest('[wire\\:id]') : null
      wireId = root ? root.getAttribute('wire:id') : null

      const onPointerDown = (event) => {
        if (!this.$root.contains(event.target)) {
          this.flush()
        }
      }

      // Capture phase: the flush must be queued BEFORE the click reaches a
      // Livewire button, so its commit is sent after the attendance save.
      document.addEventListener('pointerdown', onPointerDown, true)
      stopListening = () => document.removeEventListener('pointerdown', onPointerDown, true)
    },

    /** Keyboard pendant of the pointer flush: tabbing out of the list saves too. */
    focusLeft(event) {
      const next = event?.relatedTarget
      if (next && !this.$root.contains(next)) {
        this.flush()
      }
    },

    valueFor(personId) {
      return this.attendance[String(personId)] ?? null
    },

    isSelected(personId, value) {
      return this.valueFor(personId) === value
    },

    canClear(personId) {
      return this.allowClear && this.valueFor(personId) !== null
    },

    select(personId, value) {
      this._apply(personId, value)
    },

    /**
     * Back to "not recorded yet" — a mis-click stays correctable and the list
     * keeps showing which people have not been checked yet. Completing the
     * meeting requires the value again, so this is refused server-side once the
     * meeting is completed (the button is not rendered there either).
     */
    clear(personId) {
      if (!this.allowClear) {
        return
      }

      this._apply(personId, null)
    },

    _apply(personId, value) {
      const key = String(personId)

      if ((this.attendance[key] ?? null) === value) {
        return
      }

      this.attendance = { ...this.attendance, [key]: value }
      this.queueSave()
    },

    queueSave() {
      // generation counter: an older save finishing while a newer change is
      // already queued/in flight must not flash "saved" (or clear "saving") —
      // only the save that covers the LATEST state may report success
      const generation = ++this._saveGeneration
      this.saving = true
      this.savedFlash = false
      clearTimeout(this._saveTimer)
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null
        this._send(generation)
      }, debounce)
    },

    /** Persist a pending change immediately instead of waiting out the debounce. */
    flush() {
      if (!this._saveTimer) {
        return
      }

      clearTimeout(this._saveTimer)
      this._saveTimer = null
      this._send(this._saveGeneration)
    },

    _send(generation) {
      // serialize saves: rapid changes may schedule a new save while the
      // previous request is still in flight — chaining keeps the server
      // applying them in order, so the last (complete) state wins
      this._saveChain = (this._saveChain ?? Promise.resolve())
        .then(() => this._performSave(generation))
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    async _performSave(generation) {
      try {
        const wire = this._livewire()
        if (!wire) throw new Error('meetingAttendancePicker: Livewire component not resolvable')

        const result = await wire[saveMethod]({ ...this.attendance })

        // The server answers with an explicit false when it refused the change
        // (e.g. clearing an attendance of an already completed meeting). It
        // reported the reason itself — just never flash "saved" on top of it.
        if (result === false) {
          return
        }

        // success of the LATEST generation only — a superseded save must never
        // flash "saved"
        if (generation === this._saveGeneration) {
          this.savedFlash = true
          clearTimeout(this._flashTimer)
          this._flashTimer = setTimeout(() => { this.savedFlash = false }, 2500)
        }
      } catch (error) {
        // no silent failing — but no rethrow either, a rejected chain would
        // block all future saves. Toast via window event (NOT this.$dispatch:
        // an element-bound dispatch from a removed element never bubbles).
        window.dispatchEvent(new CustomEvent('notify', {
          detail: { type: 'danger', title: this.errorTitle, message: this.errorMessage }
        }))
        Sentry.captureException(error)
        console.error(error)
      } finally {
        if (generation === this._saveGeneration) this.saving = false
      }
    },

    destroy() {
      // flush a still-pending save instead of dropping the change
      this.flush()
      clearTimeout(this._flashTimer)
      stopListening?.()
      stopListening = null
    }
  }
}
